import { useEffect, useState } from 'react'
import {
  Button, Dialog, Field, Heading, Input, SimpleGrid, Spinner, Stack, Text, VStack, CloseButton,
} from '@chakra-ui/react'
import { Card } from '../shared/Card'
import { ResponsiveModal } from '../shared/ResponsiveModal'
import { MarkdownEditor } from './MarkdownEditor'
import { useLogistics, useSaveLogistics } from '../../hooks/useLobbyDay'
import type { LogisticsDto, SaveLogisticsRequest } from '../../types/lobbyDay'
import { toaster } from '@/components/ui/toaster'

const labelProps = { fontSize: 'sm', color: 'muted' } as const

const EMPTY_FORM: SaveLogisticsRequest = {
  mealsOwner: '', mealsNotes: '', lodgingOwner: '', lodgingNotes: '',
  transportationOwner: '', transportationNotes: '', generalNotes: '',
  dayOfPocName: '', dayOfPocPhone: '',
}

function toForm(dto: LogisticsDto): SaveLogisticsRequest {
  return {
    mealsOwner: dto.mealsOwner ?? '', mealsNotes: dto.mealsNotes ?? '',
    lodgingOwner: dto.lodgingOwner ?? '', lodgingNotes: dto.lodgingNotes ?? '',
    transportationOwner: dto.transportationOwner ?? '', transportationNotes: dto.transportationNotes ?? '',
    generalNotes: dto.generalNotes ?? '',
    dayOfPocName: dto.dayOfPocName ?? '', dayOfPocPhone: dto.dayOfPocPhone ?? '',
  }
}

interface LogisticsModalProps {
  eventId: string
  isOpen: boolean
  onClose: () => void
}

/** LW-14: logistics input moved off its own tab into a pull-up from the Schedule tab. Same fields
 *  as the retired LogisticsTab; Word-import dropped (nobody imports meals/lodging from a Word doc). */
export function LogisticsModal({ eventId, isOpen, onClose }: LogisticsModalProps) {
  const { data, isLoading, error } = useLogistics(eventId)
  const saveMutation = useSaveLogistics(eventId)
  const [form, setForm] = useState<SaveLogisticsRequest>(EMPTY_FORM)

  // Re-seed whenever the record loads or the modal (re)opens, so a Cancel-then-reopen shows the
  // persisted state, not stale local edits.
  useEffect(() => { if (isOpen && data) setForm(toForm(data)) }, [isOpen, data])

  const set = (patch: Partial<SaveLogisticsRequest>) => setForm((f) => ({ ...f, ...patch }))

  const onSave = () =>
    saveMutation.mutate(form, {
      onSuccess: () => {
        toaster.create({ title: 'Logistics saved', type: 'success', duration: 2000 })
        onClose()
      },
      onError: (e: unknown) => {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        toaster.create({ title: msg ?? 'Failed to save logistics', type: 'error', duration: 4000 })
      },
    })

  const section = (
    title: string, owner: keyof SaveLogisticsRequest, notes: keyof SaveLogisticsRequest,
    ownerPlaceholder: string,
  ) => (
    <Card>
      <Heading size="sm" mb={3}>{title}</Heading>
      <Stack gap={3}>
        <Field.Root>
          <Field.Label {...labelProps}>Who's responsible</Field.Label>
          <Input
            aria-label={`${title} owner`}
            value={form[owner] ?? ''}
            placeholder={ownerPlaceholder}
            onChange={(e) => set({ [owner]: e.target.value })}
            size="sm"
          />
        </Field.Root>
        <Field.Root>
          <Field.Label {...labelProps}>Details</Field.Label>
          <MarkdownEditor
            value={form[notes] ?? ''} onChange={(v) => set({ [notes]: v })} rows={4}
            allowWordImport={false}
          />
        </Field.Root>
      </Stack>
    </Card>
  )

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Edit logistics</Dialog.Title>
          </Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            {isLoading ? (
              <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
            ) : error ? (
              <Text color="danger.fg">Failed to load logistics.</Text>
            ) : (
              <VStack align="stretch" gap={5}>
                <Text color="muted" fontSize="sm">
                  Meals, lodging, transportation, and day-of details for this event.
                </Text>

                <SimpleGrid columns={{ base: 1, lg: 3 }} gap={4}>
                  {section('Meals', 'mealsOwner', 'mealsNotes', 'e.g. Sarah / Local 512')}
                  {section('Lodging', 'lodgingOwner', 'lodgingNotes', 'who books the block')}
                  {section('Transportation', 'transportationOwner', 'transportationNotes', 'van / carpool lead')}
                </SimpleGrid>

                <Card>
                  <Heading size="sm" mb={3}>General Notes</Heading>
                  <MarkdownEditor
                    value={form.generalNotes ?? ''} onChange={(v) => set({ generalNotes: v })} rows={6}
                    allowWordImport={false}
                  />
                </Card>

                <Card>
                  <Heading size="sm" mb={3}>Day-of Contact</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                    <Field.Root>
                      <Field.Label {...labelProps}>Name</Field.Label>
                      <Input aria-label="Day-of contact name" value={form.dayOfPocName ?? ''}
                        onChange={(e) => set({ dayOfPocName: e.target.value })} size="sm" />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label {...labelProps}>Phone</Field.Label>
                      <Input aria-label="Day-of contact phone" value={form.dayOfPocPhone ?? ''}
                        onChange={(e) => set({ dayOfPocPhone: e.target.value })} size="sm" />
                    </Field.Root>
                  </SimpleGrid>
                </Card>
              </VStack>
            )}
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorPalette="brand" onClick={onSave} loading={saveMutation.isPending}>Save</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
